import { useEffect, useRef, useState } from 'react';
import {
  getCollectionScanUploadUrl, uploadCollectionScanFile, registerCollectionScan,
  listCollectionScans, getCollectionScan, listCollectionScanItems,
  updateCollectionScanItem, applyCollectionScan,
} from '@/api/collectionReview';
import type { CollectionScan, CollectionScanItem, MonthValue } from '@/api/collectionReview';

const POLL_INTERVAL_MS = 4000;
type MonthField = 'jan' | 'feb' | 'mar' | 'apr' | 'may' | 'jun' | 'jul' | 'aug' | 'sep' | 'oct' | 'nov' | 'dec';
const MONTHS: { statusKey: keyof CollectionScanItem; amountKey: keyof CollectionScanItem; field: MonthField; label: string }[] = [
  { statusKey: 'jan_extracted', amountKey: 'jan_amount', field: 'jan', label: 'Jan' },
  { statusKey: 'feb_extracted', amountKey: 'feb_amount', field: 'feb', label: 'Feb' },
  { statusKey: 'mar_extracted', amountKey: 'mar_amount', field: 'mar', label: 'Mar' },
  { statusKey: 'apr_extracted', amountKey: 'apr_amount', field: 'apr', label: 'Apr' },
  { statusKey: 'may_extracted', amountKey: 'may_amount', field: 'may', label: 'May' },
  { statusKey: 'jun_extracted', amountKey: 'jun_amount', field: 'jun', label: 'Jun' },
  { statusKey: 'jul_extracted', amountKey: 'jul_amount', field: 'jul', label: 'Jul' },
  { statusKey: 'aug_extracted', amountKey: 'aug_amount', field: 'aug', label: 'Aug' },
  { statusKey: 'sep_extracted', amountKey: 'sep_amount', field: 'sep', label: 'Sep' },
  { statusKey: 'oct_extracted', amountKey: 'oct_amount', field: 'oct', label: 'Oct' },
  { statusKey: 'nov_extracted', amountKey: 'nov_amount', field: 'nov', label: 'Nov' },
  { statusKey: 'dec_extracted', amountKey: 'dec_amount', field: 'dec', label: 'Dec' },
];

const StatusPill = ({ status }: { status: CollectionScan['status'] }) => {
  const cls =
    status === 'ReviewPending' || status === 'Applied' ? 'bg-green-100 text-green-800' :
    status === 'Failed'                                ? 'bg-red-100 text-red-700' :
                                                           'bg-blue-100 text-blue-800';
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full font-georgia ${cls}`}>
      {(status === 'Uploaded' || status === 'Processing') && (
        <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />
      )}
      {status}
    </span>
  );
};

const MonthCell = ({ status, amount, onChangeStatus, onChangeAmount }: {
  status: MonthValue; amount: number | null;
  onChangeStatus: (v: MonthValue) => void;
  onChangeAmount: (v: number | null) => void;
}) => {
  const cls =
    status === 'Paid'   ? 'bg-green-100 text-green-800 border-green-300' :
    status === 'Unpaid' ? 'bg-red-50 text-red-700 border-red-200' :
                           'bg-gray-50 text-gray-400 border-gray-200';
  return (
    <div className="flex flex-col gap-0.5 min-w-0">
      <select
        value={status ?? ''}
        onChange={(e) => onChangeStatus((e.target.value || null) as MonthValue)}
        className={`text-[11px] font-georgia border rounded px-0 py-1 w-full text-center ${cls}`}
      >
        <option value="">—</option>
        <option value="Paid">Paid</option>
        <option value="Unpaid">Unpaid</option>
      </select>
      {status === 'Paid' && (
        <input
          type="number"
          min={0}
          placeholder="₹"
          defaultValue={amount ?? ''}
          onBlur={(e) => onChangeAmount(e.target.value === '' ? null : parseFloat(e.target.value))}
          className="text-[11px] border rounded px-0 py-0.5 w-full text-center"
        />
      )}
    </div>
  );
};

export default function CollectionReviewPage() {
  const [scans, setScans] = useState<CollectionScan[]>([]);
  const [loadingScans, setLoadingScans] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const [activeScanId, setActiveScanId] = useState<number | null>(null);
  const [items, setItems] = useState<CollectionScanItem[]>([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [applying, setApplying] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const pollingIds = useRef<Set<number>>(new Set());

  const refreshScans = () => {
    listCollectionScans({ pageSize: 25 })
      .then((r) => {
        setScans(r.items);
        r.items.forEach((s) => {
          if (s.status === 'Uploaded' || s.status === 'Processing') pollingIds.current.add(s.id);
        });
      })
      .catch(() => setError('Failed to load scan history'))
      .finally(() => setLoadingScans(false));
  };

  useEffect(() => { refreshScans(); }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      if (pollingIds.current.size === 0) return;
      pollingIds.current.forEach((id) => {
        getCollectionScan(id).then((scan) => {
          setScans((prev) => prev.map((s) => (s.id === scan.id ? scan : s)));
          if (scan.status !== 'Uploaded' && scan.status !== 'Processing') {
            pollingIds.current.delete(id);
            if (scan.status === 'ReviewPending' && scan.id === activeScanId) loadItems(scan.id);
          }
        }).catch(() => pollingIds.current.delete(id));
      });
    }, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeScanId]);

  const handleFileChosen = async (file: File | null) => {
    if (!file) return;
    setError('');
    setUploading(true);
    try {
      const { uploadUrl, s3Key } = await getCollectionScanUploadUrl();
      await uploadCollectionScanFile(uploadUrl, file);
      const scan = await registerCollectionScan(s3Key);
      pollingIds.current.add(scan.id);
      setScans((prev) => [scan, ...prev]);
    } catch {
      setError('Failed to upload scan — please try again');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const loadItems = (scanId: number) => {
    setActiveScanId(scanId);
    setLoadingItems(true);
    listCollectionScanItems(scanId)
      .then(setItems)
      .catch(() => setError('Failed to load extracted items'))
      .finally(() => setLoadingItems(false));
  };

  const patchItem = (id: number, patch: Partial<CollectionScanItem>) => {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  };

  const saveItem = async (item: CollectionScanItem, overrides: Partial<CollectionScanItem> = {}) => {
    const merged = { ...item, ...overrides };
    try {
      const saved = await updateCollectionScanItem(item.id, {
        jan: merged.jan_extracted, feb: merged.feb_extracted, mar: merged.mar_extracted, apr: merged.apr_extracted,
        may: merged.may_extracted, jun: merged.jun_extracted, jul: merged.jul_extracted, aug: merged.aug_extracted,
        sep: merged.sep_extracted, oct: merged.oct_extracted, nov: merged.nov_extracted, dec: merged.dec_extracted,
        janAmount: merged.jan_amount, febAmount: merged.feb_amount, marAmount: merged.mar_amount, aprAmount: merged.apr_amount,
        mayAmount: merged.may_amount, junAmount: merged.jun_amount, julAmount: merged.jul_amount, augAmount: merged.aug_amount,
        sepAmount: merged.sep_amount, octAmount: merged.oct_amount, novAmount: merged.nov_amount, decAmount: merged.dec_amount,
        cemeteryText: merged.cemetery_text_extracted,
        reviewedStatus: merged.reviewed_status,
        familyId: merged.family_id,
      });
      patchItem(item.id, saved);
    } catch {
      setError('Failed to save review — please retry');
    }
  };

  const applyScan = async () => {
    if (!activeScanId) return;
    setApplying(true);
    setError('');
    try {
      const result = await applyCollectionScan(activeScanId);
      loadItems(activeScanId);
      refreshScans();
      alert(`Applied ${result.itemsApplied} approved row(s): ${result.monthsApplied} month(s) updated, ${result.cemeteryRowsApplied} cemetery record(s) added.`);
    } catch {
      setError('Failed to apply approved rows — please retry');
    } finally {
      setApplying(false);
    }
  };

  const approvedUnappliedCount = items.filter((i) => i.reviewed_status === 'Approved' && !i.applied_at).length;

  return (
    <div className="p-4 md:p-6 w-full">
      <h1 className="text-2xl font-georgia font-bold text-navy mb-1">Collection Sheet Scans</h1>
      <p className="text-sm text-gray-600 mb-6 max-w-2xl">
        Upload a scanned PDF of filled-in Family Collection Sheets. The system reads each row automatically,
        but nothing is written to live records until you review and approve it below.
      </p>

      {error && (
        <div className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2 font-georgia">
          {error}
        </div>
      )}

      <section className="bg-white border rounded-lg p-4 mb-6">
        <h2 className="text-sm font-georgia font-semibold text-navy mb-3">Upload a scan</h2>
        <input
          ref={fileInputRef}
          type="file"
          accept="application/pdf"
          disabled={uploading}
          onChange={(e) => handleFileChosen(e.target.files?.[0] ?? null)}
          className="text-sm font-georgia"
        />
        {uploading && <span className="ml-3 text-xs text-gray-500">Uploading…</span>}
      </section>

      <section className="mb-8">
        <h2 className="text-sm font-georgia font-semibold text-navy mb-3">Scan history</h2>
        <div className="bg-white border rounded-lg overflow-hidden overflow-x-auto">
          <table className="w-full text-sm font-georgia">
            <thead>
              <tr className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                <th className="px-4 py-2">Scan</th>
                <th className="px-4 py-2">Uploaded by</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2">Uploaded</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {scans.map((scan) => (
                <tr key={scan.id} className="border-t">
                  <td className="px-4 py-2.5 font-mono text-xs">#SCAN-{String(scan.id).padStart(4, '0')}</td>
                  <td className="px-4 py-2.5">{scan.uploaded_by}</td>
                  <td className="px-4 py-2.5"><StatusPill status={scan.status} /></td>
                  <td className="px-4 py-2.5 text-xs text-gray-500">{new Date(scan.created_at).toLocaleString()}</td>
                  <td className="px-4 py-2.5">
                    {(scan.status === 'ReviewPending' || scan.status === 'Applied') && (
                      <button
                        type="button"
                        onClick={() => loadItems(scan.id)}
                        className="text-teal font-semibold hover:underline text-xs"
                      >
                        Review
                      </button>
                    )}
                    {scan.status === 'Failed' && (
                      <span className="text-red-600 text-xs" title={scan.error_message ?? ''}>Failed</span>
                    )}
                  </td>
                </tr>
              ))}
              {!loadingScans && scans.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-6 text-center text-gray-500">No scans uploaded yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {activeScanId && (
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-georgia font-semibold text-navy">
              Review scan #SCAN-{String(activeScanId).padStart(4, '0')}
              <span className="ml-2 text-xs text-gray-500 font-normal">
                (lowest-confidence rows shown first)
              </span>
            </h2>
            <button
              type="button"
              onClick={applyScan}
              disabled={applying || approvedUnappliedCount === 0}
              className="bg-teal text-white font-georgia font-semibold text-sm px-4 py-2 rounded hover:bg-teal/90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {applying ? 'Applying…' : `Apply ${approvedUnappliedCount} approved row(s)`}
            </button>
          </div>

          {loadingItems && <p className="text-sm text-gray-500 font-georgia">Loading extracted rows…</p>}

          {/* Fixed layout: the twelve month columns share whatever width is
              left, so the whole row fits without scrolling sideways. */}
          <div className="bg-white border rounded-lg">
            <table className="w-full table-fixed text-xs font-georgia">
              <colgroup>
                <col className="w-[9rem]" />
                <col className="w-[3.25rem]" />
                <col className="w-[3.25rem]" />
                {MONTHS.map((m) => <col key={m.field} />)}
                <col className="w-[8.5rem]" />
                <col className="w-[6.5rem]" />
              </colgroup>
              <thead>
                <tr className="bg-gray-50 text-left uppercase tracking-wide text-gray-500">
                  <th className="px-2 py-2">Family</th>
                  <th className="px-1 py-2">Year</th>
                  <th className="px-1 py-2" title="Confidence">Conf.</th>
                  {MONTHS.map((m) => <th key={m.field} className="px-0.5 py-2 text-center">{m.label}</th>)}
                  <th className="px-2 py-2">Cemetery note</th>
                  <th className="px-2 py-2">Decision</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => {
                  const isLowConfidence = item.overall_confidence != null && item.overall_confidence < 70;
                  return (
                    <tr key={item.id} className={`border-t ${isLowConfidence ? 'bg-amber-50' : ''}`}>
                      <td className="px-2 py-2 break-words">
                        {item.matchedFamilyCode
                          ? <span className="font-semibold">{item.matchedFamilyCode}</span>
                          : <span className="text-red-600" title="Printed family code did not match any family">{item.family_code_extracted || '(unreadable)'} ⚠</span>}
                        <div className="text-gray-500">{item.matchedFamilyHead}</div>
                      </td>
                      <td className="px-1 py-2">{item.subscription_year}</td>
                      <td className="px-1 py-2">{item.overall_confidence != null ? `${Math.round(item.overall_confidence)}%` : '—'}</td>
                      {MONTHS.map((m) => (
                        <td key={m.field} className="px-0.5 py-1">
                          <MonthCell
                            status={item[m.statusKey] as MonthValue}
                            amount={item[m.amountKey] as number | null}
                            onChangeStatus={(v) => { patchItem(item.id, { [m.statusKey]: v } as Partial<CollectionScanItem>); saveItem(item, { [m.statusKey]: v } as Partial<CollectionScanItem>); }}
                            onChangeAmount={(v) => { patchItem(item.id, { [m.amountKey]: v } as Partial<CollectionScanItem>); saveItem(item, { [m.amountKey]: v } as Partial<CollectionScanItem>); }}
                          />
                        </td>
                      ))}
                      <td className="px-2 py-2">
                        <input
                          type="text"
                          defaultValue={item.cemetery_text_extracted ?? ''}
                          onBlur={(e) => saveItem(item, { cemetery_text_extracted: e.target.value || null })}
                          className="text-xs border rounded px-1.5 py-1 w-full"
                          placeholder="(none)"
                        />
                      </td>
                      <td className="px-2 py-2">
                        <select
                          value={item.reviewed_status}
                          disabled={!item.matchedFamilyCode}
                          onChange={(e) => saveItem(item, { reviewed_status: e.target.value as CollectionScanItem['reviewed_status'] })}
                          className="text-xs border rounded px-1 py-1 w-full"
                        >
                          <option value="Pending">Pending</option>
                          <option value="Approved">Approve</option>
                          <option value="Rejected">Reject</option>
                        </select>
                        {item.applied_at && <div className="text-green-700 mt-1">Applied</div>}
                      </td>
                    </tr>
                  );
                })}
                {!loadingItems && items.length === 0 && (
                  <tr><td colSpan={17} className="px-4 py-6 text-center text-gray-500">No extracted rows</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}
